import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';

import { Img } from 'ui-kit';
import { CT_SYMBOL, NOTIFICATION_BELL_ICON } from 'assets/icons';
import { AuthEntity, CollectEntity, NotificationEntity } from '_entities';
import { NotificationModal, UserInformationModal } from 'modules/Modals';
import LogOutPopup from 'modules/Navigation/MobileChrome/components/LogOutPopup';
import { IsAzAccount } from 'modules';
import { usePopover, useRefreshNotificationsOnMediaExport } from 'hooks';
import { idbService, notification } from 'utils/services';
import { removeToken } from 'utils/services/token';
import { signOut } from '_entities/Auth/Auth.actions';
import { baseUrl } from 'lib/baseUrl';
import { PERMISSIONS, URLS, USER_ROLE_TYPES } from '_constants';

import { getMenuRoutes, filterRoutesByPermissions } from '../_utils';
import CommandPalette from '../CommandPalette';
import Drawer from './Drawer';
import * as Styled from './styles';

const {
  getAuthenticatedUser,
  getUserEmail,
  getUserFirstName,
  getUserLastName,
  getAccountProductInfo,
  getProfilePermissions,
  getModularityPermissions,
  getUserRoleType,
  getDashboardLinks,
  getCanAccessAppDashboard,
  getIsAppDashboardOnlyUser,
} = AuthEntity.selectors;
const { setCollectCount } = CollectEntity.actions;
const { getNotificationList } = NotificationEntity.actions;
const { getNotificationMessages, getUnreadMessages } = NotificationEntity.selectors;
const { getCollectCount } = CollectEntity.selectors;

const REPORT_POLL_TTL_MS = 10 * 60 * 1000;
const REPORT_POLL_INTERVAL_MS = 20_000;

const checkPollingTimeout = () => {
  const lastReportTime = localStorage.getItem('lastReportTime');
  if (!lastReportTime) return false;
  const last = Number(lastReportTime);
  if (!Number.isFinite(last)) return false;
  return Date.now() < last + REPORT_POLL_TTL_MS;
};

const hasPendedReports = () => {
  try {
    const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
    return generatedReports.length > 0;
  } catch {
    return false;
  }
};

const fetchTaskStatus = (taskId) => {
  const token = localStorage.getItem('Authorization');
  return axios.get(`${baseUrl}tasks/${taskId}/status/`, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/vnd.api+json',
    },
  });
};

const dropTaskIdFromStorage = (taskId) => {
  const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
  const remaining = generatedReports.filter((id) => id !== taskId);
  if (remaining.length === 0) {
    localStorage.removeItem('generatedReports');
    localStorage.removeItem('lastReportTime');
  } else {
    localStorage.setItem('generatedReports', JSON.stringify(remaining));
  }
};

const initialsFromName = (firstName, lastName, email) => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f || l) return `${f[0] || ''}${l[0] || ''}`.toUpperCase() || '??';
  if (!email) return '??';
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};

const HamburgerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SearchIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="m20 20-4.2-4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.25" />
    <circle cx="8" cy="5" r="0.85" fill="currentColor" />
    <path d="M8 7.5v4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const UserAddIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="6.5" cy="5.25" r="2.5" stroke="currentColor" strokeWidth="1.25" />
    <path
      d="M2 13c0-2.2 2-3.75 4.5-3.75S11 10.8 11 13"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
    <path d="M12.5 8.25v3M14 9.75h-3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M9.5 3.5V2.75A1.25 1.25 0 0 0 8.25 1.5h-4A1.25 1.25 0 0 0 3 2.75v10.5a1.25 1.25 0 0 0 1.25 1.25h4a1.25 1.25 0 0 0 1.25-1.25V12.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
    <path
      d="M6.5 8h7.5M11.5 5.5 14 8l-2.5 2.5"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobileChrome = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const isAuthenticated = useSelector(getAuthenticatedUser);
  const userEmail = useSelector(getUserEmail);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const accountProduct = useSelector(getAccountProductInfo);
  const userPermissions = useSelector(getProfilePermissions);
  const modularityPermissions = useSelector(getModularityPermissions);
  const userRoleType = useSelector(getUserRoleType);
  const canAccessAppDashboard = useSelector(getCanAccessAppDashboard);
  const isAppDashboardOnlyUser = useSelector(getIsAppDashboardOnlyUser);
  const dashboardInfoSections = useSelector(getDashboardLinks);
  const notifications = useSelector(getNotificationMessages);
  const unreadMessages = useSelector(getUnreadMessages);
  const collectCount = useSelector(getCollectCount);
  const isAzAccount = IsAzAccount();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userInfoOpen, setUserInfoOpen] = useState(false);
  const [logOutOpen, setLogOutOpen] = useState(false);
  const pollIntervalRef = useRef(null);
  const accountWrapperRef = useRef(null);
  const accountPanelRef = useRef(null);
  const {
    open: accountOpen,
    setOpen: setAccountOpen,
    close: closeAccount,
  } = usePopover({
    wrapperRef: accountWrapperRef,
    panelRef: accountPanelRef,
  });

  const initials = useMemo(() => initialsFromName(firstName, lastName, userEmail), [firstName, lastName, userEmail]);
  const fullName = useMemo(() => {
    const joined = `${firstName || ''} ${lastName || ''}`.trim();
    return joined || userEmail || '';
  }, [firstName, lastName, userEmail]);

  const sections = useMemo(() => {
    if (!isAuthenticated) {
      const routes = getMenuRoutes(false, '', false, [], isAzAccount);
      return filterRoutesByPermissions(routes, userPermissions, modularityPermissions);
    }
    const routes = getMenuRoutes(
      true,
      userEmail,
      false,
      dashboardInfoSections,
      isAzAccount,
      canAccessAppDashboard,
      isAppDashboardOnlyUser,
    );
    return filterRoutesByPermissions(routes, userPermissions, modularityPermissions).filter(
      (item) => typeof item.id === 'number' && item.id <= 6,
    );
  }, [
    isAuthenticated,
    userEmail,
    dashboardInfoSections,
    isAzAccount,
    canAccessAppDashboard,
    isAppDashboardOnlyUser,
    userPermissions,
    modularityPermissions,
  ]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const toggleNotifications = useCallback(() => setNotificationsOpen((prev) => !prev), []);
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);
  const toggleAccount = useCallback(() => setAccountOpen((prev) => !prev), [setAccountOpen]);

  const openUserDetails = useCallback(() => {
    closeAccount();
    setUserInfoOpen(true);
  }, [closeAccount]);

  const openLogOut = useCallback(() => {
    closeAccount();
    setLogOutOpen(true);
  }, [closeAccount]);

  const handleLoginAsUser = useCallback(async () => {
    closeAccount();
    try {
      await dispatch(signOut());
    } catch {
      // Non-fatal: token is still cleared and login flow continues below.
    } finally {
      removeToken();
      history.push(URLS.login, { as_user: true });
    }
  }, [closeAccount, dispatch, history]);

  const canLoginAsUser =
    userRoleType === USER_ROLE_TYPES.MULTI_ACCOUNT_MANAGER &&
    Array.isArray(userPermissions) &&
    userPermissions.includes(PERMISSIONS.LOGIN_AS_USER);

  const loadNotifications = useCallback(async () => {
    try {
      await dispatch(getNotificationList());
    } catch {
      // Non-fatal; bell badge stays at its last value.
    }
  }, [dispatch]);

  useEffect(() => {
    const updateOfflineSurveyCount = async () => {
      const db = await idbService.initiateIDB();
      const count = await db.transaction('surveys').objectStore('surveys').count();
      await dispatch(setCollectCount(count));
    };
    updateOfflineSurveyCount();
    if (isAuthenticated) loadNotifications();
    // Mount-only effect; matches the pre-refactor behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the bell current when a media export lands while the user stays on this page — the mount-only
  // fetch above is otherwise the last word until a reload or a route change.
  useRefreshNotificationsOnMediaExport(loadNotifications, isAuthenticated);

  useEffect(() => {
    if (unreadMessages && !notificationsOpen && isAuthenticated && pathname === URLS.dashboard) {
      const noun = unreadMessages > 1 ? 'messages' : 'message';
      notification.info({
        notification: `You have ${unreadMessages} unread ${noun}!`,
        onClick: toggleNotifications,
      });
    }
  }, [unreadMessages, notificationsOpen, isAuthenticated, pathname, toggleNotifications]);

  useEffect(() => {
    const pollOnce = async () => {
      if (!checkPollingTimeout()) {
        localStorage.removeItem('generatedReports');
        localStorage.removeItem('lastReportTime');
        return;
      }
      if (!hasPendedReports()) return;
      const generatedReports = JSON.parse(localStorage.getItem('generatedReports') || '[]');
      await Promise.all(
        generatedReports.map(async (taskId) => {
          try {
            const response = await fetchTaskStatus(taskId);
            const status = response?.data?.data?.status;
            if (status === 'SUCCESS' || status === 'ERROR') {
              dropTaskIdFromStorage(taskId);
              await loadNotifications();
              if (status === 'SUCCESS') {
                notification.info({
                  notification: 'Your report is successfully generated and ready to download!',
                  onClick: toggleNotifications,
                  autoClose: { autoClose: false },
                });
              }
            }
          } catch {
            // Non-fatal; tick again on next interval.
          }
        }),
      );
      if (!hasPendedReports() && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    if (isAuthenticated && checkPollingTimeout()) {
      pollOnce();
      pollIntervalRef.current = setInterval(pollOnce, REPORT_POLL_INTERVAL_MS);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, loadNotifications, toggleNotifications]);

  // Close drawer when route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  const handleSearchClick = useCallback(() => {
    openPalette();
  }, [openPalette]);

  return (
    <Styled.Root className="no-print">
      {/* data-app-header: see TopBar — lets useAnchoredPanel hide popovers sliding under the sticky bar. */}
      <Styled.TopBar data-app-header>
        <Styled.BrandGroup>
          <Img alt="" src={CT_SYMBOL} size={[24, 24]} />
          <Styled.BrandName>Counter Tools</Styled.BrandName>
          {accountProduct && <Styled.AccountProduct>{accountProduct}</Styled.AccountProduct>}
        </Styled.BrandGroup>

        <Styled.RightCluster>
          {isAuthenticated && (
            <Styled.IconButton type="button" onClick={toggleNotifications} aria-label="Notifications">
              <Img size={[14, 14]} src={NOTIFICATION_BELL_ICON} alt="" />
              {Boolean(unreadMessages) && (
                <Styled.BellBadge>{unreadMessages <= 9 ? unreadMessages : '9+'}</Styled.BellBadge>
              )}
            </Styled.IconButton>
          )}

          {isAuthenticated ? (
            <Styled.AccountWrapper ref={accountWrapperRef}>
              <Styled.AvatarCircle
                type="button"
                onClick={toggleAccount}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                aria-label="Account"
              >
                {initials}
              </Styled.AvatarCircle>

              {accountOpen && (
                <Styled.AccountPanel ref={accountPanelRef} role="menu" aria-label="Account">
                  <Styled.AccountHeader>
                    <Styled.AccountHeaderAvatar>{initials}</Styled.AccountHeaderAvatar>
                    <Styled.AccountHeaderText>
                      {fullName && <Styled.AccountName>{fullName}</Styled.AccountName>}
                      {userEmail && <Styled.AccountEmail>{userEmail}</Styled.AccountEmail>}
                    </Styled.AccountHeaderText>
                  </Styled.AccountHeader>

                  <Styled.AccountDivider />

                  <Styled.AccountItem type="button" role="menuitem" onClick={openUserDetails}>
                    <Styled.AccountItemIcon>
                      <InfoIcon />
                    </Styled.AccountItemIcon>
                    User details
                  </Styled.AccountItem>

                  {canLoginAsUser && (
                    <Styled.AccountItem type="button" role="menuitem" onClick={handleLoginAsUser}>
                      <Styled.AccountItemIcon>
                        <UserAddIcon />
                      </Styled.AccountItemIcon>
                      Login as user…
                    </Styled.AccountItem>
                  )}

                  <Styled.AccountDivider />

                  <Styled.AccountItem type="button" role="menuitem" onClick={openLogOut} $danger>
                    <Styled.AccountItemIcon>
                      <LogoutIcon />
                    </Styled.AccountItemIcon>
                    Log out
                  </Styled.AccountItem>
                </Styled.AccountPanel>
              )}
            </Styled.AccountWrapper>
          ) : null}

          {sections.length > 0 && (
            <Styled.IconButton type="button" onClick={openDrawer} aria-label="Open navigation">
              <HamburgerIcon />
            </Styled.IconButton>
          )}
        </Styled.RightCluster>
      </Styled.TopBar>

      {isAuthenticated && (
        <Styled.SearchRow>
          <Styled.SearchTrigger type="button" onClick={handleSearchClick} aria-label="Open command palette">
            <Styled.SearchIcon>
              <SearchIconSvg />
            </Styled.SearchIcon>
            <Styled.SearchPlaceholder>Jump to anything · retailer, inspection…</Styled.SearchPlaceholder>
          </Styled.SearchTrigger>
        </Styled.SearchRow>
      )}

      {drawerOpen && (
        <Drawer sections={sections} currentPathname={pathname} collectCount={collectCount} onClose={closeDrawer} />
      )}

      {paletteOpen && <CommandPalette onClose={closePalette} />}

      {notificationsOpen && (
        <NotificationModal
          onClose={closeNotifications}
          notificationList={notifications}
          loadNotifications={loadNotifications}
        />
      )}

      <UserInformationModal isOpen={userInfoOpen} onClose={() => setUserInfoOpen(false)} />
      <LogOutPopup isOpenPopup={logOutOpen} setIsOpenPopup={setLogOutOpen} />
    </Styled.Root>
  );
};

export default MobileChrome;
